import React, { useEffect, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import axios from "axios";
import { CheckCircle2, ArrowRight, Phone, Mail } from "lucide-react";
import { useSettings } from "@/context/SettingsContext";
import { Reveal } from "@/lib/motion";
import { imgSrc } from "@/lib/img";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function OrderConfirmation() {
  const { orderNumber } = useParams();
  const [params] = useSearchParams();
  const settings = useSettings();
  const [order, setOrder] = useState(null);
  const [missing, setMissing] = useState(false);

  useEffect(() => {
    const email = params.get("email") || JSON.parse(localStorage.getItem("nb_last_order") || "{}").email || "";
    axios.get(`${API}/orders/${orderNumber}`, { params: { email } }).then(({ data }) => setOrder(data)).catch(() => setMissing(true));
  }, [orderNumber, params]);

  if (missing) return <div data-testid="order-not-found" className="container-nb py-32 text-center font-serif text-2xl text-forest">We couldn't find that order.</div>;
  if (!order) return <div className="min-h-[60vh]" />;

  return (
    <div data-testid="order-confirmation-page" className="py-16 sm:py-24">
      <div className="container-nb max-w-3xl">
        <Reveal>
          <div className="text-center">
            <span className="mx-auto w-16 h-16 rounded-full bg-[#E8F0EA] text-forest flex items-center justify-center"><CheckCircle2 size={30} strokeWidth={1.6} /></span>
            <p className="eyebrow mt-6 mb-3">Order received</p>
            <h1 className="font-serif text-4xl sm:text-5xl text-forest leading-[1.05]">Thank you, {order.customer.name.split(" ")[0]}.</h1>
            <p className="text-mutedgreen mt-4">Your order <span data-testid="order-number" className="font-mono font-bold text-forest">{order.order_number}</span> has been placed. Our team will confirm it and share payment options shortly.</p>
          </div>
        </Reveal>
        <Reveal delay={0.1}>
          <div className="mt-10 bg-white border border-forest/10 rounded-3xl overflow-hidden">
            <ul className="divide-y divide-forest/10">
              {order.items.map((i) => (
                <li key={`${i.product_id}-${i.variant_id || ""}`} className="flex items-center gap-4 px-6 py-4 text-sm">
                  <img src={imgSrc(i.image)} alt="" className="w-12 h-12 rounded-lg object-cover" />
                  <span className="flex-1 text-forest">{i.name} <span className="text-mutedgreen">{i.variant_label ? `· ${i.variant_label} ` : ""}× {i.qty}</span></span>
                  <span className="font-semibold text-forest">₹{i.line_total}</span>
                </li>
              ))}
            </ul>
            <div className="bg-creamdark/50 px-6 py-5 space-y-2 text-sm">
              <div className="flex justify-between text-mutedgreen"><span>Subtotal</span><span>₹{order.subtotal}</span></div>
              {order.discount > 0 && <div className="flex justify-between text-gold"><span>Discount ({order.coupon_code})</span><span>− ₹{order.discount}</span></div>}
              <div className="flex justify-between text-mutedgreen"><span>Shipping</span><span>{order.shipping > 0 ? `₹${order.shipping}` : "Free"}</span></div>
              <div className="flex justify-between font-semibold text-forest text-base pt-2 border-t border-forest/10"><span>Total</span><span data-testid="order-total">₹{order.total}</span></div>
              <p className="text-[11px] text-mutedgreen pt-1">Payment: <span className="font-semibold capitalize">{order.payment_status}</span> · Status: <span className="font-semibold capitalize">{order.status}</span></p>
            </div>
          </div>
        </Reveal>
        <Reveal delay={0.15}>
          <div className="grid sm:grid-cols-2 gap-4 mt-6">
            <div className="bg-white border border-forest/10 rounded-2xl p-5 text-sm">
              <p className="text-[10px] font-bold uppercase tracking-[0.15em] text-gold mb-2">Delivering to</p>
              <p className="text-forest font-semibold">{order.customer.name}</p>
              <p className="text-mutedgreen mt-1">{order.customer.address}, {order.customer.city}, {order.customer.state} – {order.customer.pincode}</p>
              <p className="text-mutedgreen">{order.customer.phone} · {order.customer.email}</p>
            </div>
            <div className="bg-white border border-forest/10 rounded-2xl p-5 text-sm">
              <p className="text-[10px] font-bold uppercase tracking-[0.15em] text-gold mb-2">Questions?</p>
              <p className="flex items-center gap-2 text-forest"><Phone size={13} className="text-gold" /> {settings.phone}</p>
              <p className="flex items-center gap-2 text-forest mt-1"><Mail size={13} className="text-gold" /> {settings.email}</p>
              <p className="text-mutedgreen mt-2 text-xs">{settings.hours}</p>
            </div>
          </div>
          <div className="text-center mt-10">
            <Link to="/collection" data-testid="order-continue-shopping" className="group inline-flex items-center gap-2.5 bg-forest text-cream text-xs font-semibold uppercase tracking-[0.18em] px-8 py-4 rounded-full hover:bg-gold transition-colors duration-300">Continue Shopping <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform" /></Link>
          </div>
        </Reveal>
      </div>
    </div>
  );
}
